import { ArrowRightOutlined } from '@ant-design/icons';
import { DatePicker } from '@client/components/_ui/data-entry/DatePicker';
import { Select } from 'antd';
import type { ReactNode } from 'react';
import { operatorRequiresMultipleValues, operatorRequiresValue } from '../core/operators';
import { isRelativeDateToken, RELATIVE_DATE_PRESETS } from '../core/relative-date';
import type { FieldConfig, FilterOperator } from '../core/types';
import { getFilterPopupContainer } from './filter-popup-container';

const groupHeader = (text: string): ReactNode => (
    <span className="font-semibold text-2xs text-subtle-foreground uppercase tracking-wider">{text}</span>
);

interface DateFilterValueProps {
    value: any;
    operator: FilterOperator;
    fieldConfig: FieldConfig<any, any>;
    onChange: (value: any) => void;
    autoFocus?: boolean;
}

/**
 * Value editor for date / datetime filters. The control follows the operator:
 * - `withinRelative` ("is within") → the grouped "Relative" preset dropdown (Today, Last 7/30/90
 *   days, This month, This year). The stored token is resolved to a concrete range at where-build
 *   time by the field's `transform`.
 * - `between` → two DatePickers (from → to).
 * - any other value-taking operator → a single DatePicker.
 */
export function DateFilterValue({ value, operator, fieldConfig, onChange, autoFocus }: DateFilterValueProps) {
    const isDateOnly = fieldConfig.type !== 'datetime';

    // Value-less operators (is empty / is not empty) need no control.
    if (!operatorRequiresValue(operator)) return null;

    if (operator === 'withinRelative') {
        return (
            <Select
                size="middle"
                value={isRelativeDateToken(value) ? value : undefined}
                onChange={onChange}
                placeholder="Select…"
                className="min-w-[180px]"
                popupClassName="crm-grouped-filter-popup"
                popupMatchSelectWidth={false}
                optionLabelProp="label"
                getPopupContainer={getFilterPopupContainer}
                autoFocus={autoFocus}
                options={[{ label: groupHeader('Relative'), title: 'Relative', options: RELATIVE_DATE_PRESETS }]}
            />
        );
    }

    const formatDate = (date: Date | string | null | undefined): string | null => {
        if (!date) return null;
        const dateObj = date instanceof Date ? date : new Date(date);
        return isDateOnly ? dateObj.toISOString().split('T')[0] : dateObj.toISOString();
    };
    const parseDate = (v: any): Date | null => {
        if (!v || isRelativeDateToken(v)) return null;
        return v instanceof Date ? v : new Date(v);
    };

    // Ant's DatePicker opens its own popup on focus; auto-focusing inside the chip Popover can hit a
    // nested-popup state loop, so these never auto-focus.
    if (operatorRequiresMultipleValues(operator)) {
        const [from, to] = Array.isArray(value) ? value : [null, null];
        return (
            <span className="inline-flex items-center gap-1">
                <DatePicker
                    size="middle"
                    value={parseDate(from)}
                    onChange={(date) => onChange([formatDate(date), to])}
                    className="w-[140px] bg-transparent"
                    placeholder="From"
                    dateOnly={isDateOnly}
                    autoFocus={false}
                    getPopupContainer={getFilterPopupContainer}
                />
                <ArrowRightOutlined className="text-subtle-foreground text-xs" />
                <DatePicker
                    size="middle"
                    value={parseDate(to)}
                    onChange={(date) => onChange([from, formatDate(date)])}
                    className="w-[140px] bg-transparent"
                    placeholder="To"
                    dateOnly={isDateOnly}
                    autoFocus={false}
                    getPopupContainer={getFilterPopupContainer}
                />
            </span>
        );
    }

    return (
        <DatePicker
            size="middle"
            value={parseDate(value)}
            onChange={(date) => onChange(formatDate(date))}
            className="min-w-[120px] rounded-sm bg-transparent"
            placeholder="Select date"
            dateOnly={isDateOnly}
            autoFocus={false}
            getPopupContainer={getFilterPopupContainer}
        />
    );
}
